import { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Steps } from "../../components/ui/steps";
import { fetch } from "@/src/lib/fetch";
import { boatsFetch } from "@/src/lib/boatsFetch";
import { useToast } from "../../components/ui/use-toast";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Info,
  AlertTriangle,
  FileText,
  UploadCloud,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import clsx from "clsx";
import { Offer, OfferYacht } from "./model";
import {
  STEPS,
  PROVIDER_LABEL,
  needsContract,
  REACTION,
  CARD_FEE_THRESHOLD,
  yachtFee,
  yachtSpec,
  publicHost,
  num,
  Badge,
  Row,
  Group,
  MoneyInput,
  type ParseResult,
  type ConvertForm,
} from "./convertModalParts";

/**
 * Convert offer → booking. A modal over the sent offer.
 * - NauSYS / Booking-Manager yachts: light path — option the yacht and open the
 *   booking summary (no contract). Shipped in P1-11A.
 * - CYA / Yachtfolio yachts: full MYBA/CYBA flow — upload the signed contract,
 *   parse it (POST /api/contracts/parse), validate every field, then create the
 *   booking (POST boat-service /bookings/from-contract) with expected-unpaid
 *   instalments, deal → Contract out, HubSpot writeback. P1-11B.
 */

export function ConvertToBookingModal({
  offer,
  yachts,
  open,
  onOpenChange,
}: {
  offer: Offer;
  yachts: OfferYacht[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(0);
  const [pick, setPick] = useState<number | null>(null);
  const [note, setNote] = useState("");

  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);

  const [form, setForm] = useState<ConvertForm | null>(null);
  const [commMode, setCommMode] = useState<"pct" | "amount">("pct");
  const [commPct, setCommPct] = useState(15);
  const [commAmt, setCommAmt] = useState<number | null>(null);
  const [card, setCard] = useState(false);
  const [payRef, setPayRef] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);
  const [contractWarn, setContractWarn] = useState(false);

  const noFeedback = useMemo(() => !yachts.some((y) => y.reaction), [yachts]);
  const picked = useMemo(() => yachts.find((y) => y.id === pick) ?? null, [yachts, pick]);
  const ref = offer.hubspotId && offer.hubspotId !== "NO_HUBSPOT_ID" ? offer.hubspotId : offer.id;

  const money = (n: number, code?: string) => {
    const cur = code || form?.financials.currency || offer.currency?.code || "EUR";
    // The currency is an editable free-text field — a half-typed value (e.g. "U")
    // is not a valid ISO code and Intl.NumberFormat throws, which would crash the
    // whole modal mid-render. Fall back to a plain formatted number in that case.
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(
        n || 0,
      );
    } catch {
      return `${cur} ${Math.round(n || 0).toLocaleString("en-US")}`;
    }
  };


  const reset = () => {
    setStep(0); setPick(null); setNote(""); setFile(null); setParsing(false);
    setForm(null); setCommMode("pct"); setCommPct(15); setCommAmt(null);
    setCard(false); setPayRef(""); setCreating(false); setCreatedSlug(null); setContractWarn(false);
  };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  // NauSYS / BM light path.
  const optionAndOpenSummary = () => {
    if (!picked) return;
    window.open(`${publicHost(offer.brand)}/booking/summary/${offer.id}?yacht=${picked.id}`, "_blank", "noopener,noreferrer");
    close();
  };

  // Step 1 → parse the uploaded contract.
  const parse = async () => {
    if (!file || !picked) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch<ParseResult>("/api/contracts/parse", "POST", fd);
      if (!res?.ok) {
        toast({ title: "Couldn’t parse the contract", description: res?.error?.message ?? "Try a text PDF or DOCX.", variant: "destructive" });
        setParsing(false);
        return;
      }
      const p = res.parsed;
      const cust = offer.customer;
      const fee = p.financials.charterFee ?? yachtFee(picked);
      const finTotal =
        p.financials.total ??
        fee + (p.financials.vat ?? 0) + (p.financials.apa ?? 0) + (p.financials.delivery ?? 0) + (p.financials.additionalFees ?? 0);
      // MYBA default when the contract carries no payment terms: 50% of the charter
      // fee on confirmation (today); the balance — the other 50% + APA + VAT (+ any
      // fees) — six weeks before boarding, clamped to today when closer than that.
      const defaultSchedule = () => {
        const today = new Date().toISOString().slice(0, 10);
        const first = Math.round((fee / 2) * 100) / 100;
        const balance = Math.round((finTotal - first) * 100) / 100;
        if (!fee || balance <= 0) return [{ label: "Instalment 1", amount: finTotal || fee, dueDate: today }];
        let due = today;
        if (picked.startDate) {
          const d = new Date(picked.startDate.slice(0, 10));
          d.setDate(d.getDate() - 42);
          const iso = d.toISOString().slice(0, 10);
          if (iso > today) due = iso;
        }
        return [
          { label: "Instalment 1 — 50% charter fee", amount: first, dueDate: today },
          { label: "Instalment 2 — balance + APA + VAT", amount: balance, dueDate: due },
        ];
      };
      setForm({
        template: p.template,
        filename: file.name,
        yacht: {
          name: p.yacht?.name || picked.yacht?.name || "",
          type: p.yacht?.type || [picked.yacht?.builder, picked.yacht?.boatType].filter(Boolean).join(" "),
          flag: p.yacht?.flag || "",
          length: p.yacht?.length || (picked.yacht?.length ? `${picked.yacht.length} m` : ""),
        },
        dates: {
          startDate: picked.startDate?.slice(0, 10) || "",
          endDate: picked.endDate?.slice(0, 10) || "",
          embarkTime: picked.startTime || "",
          disembarkTime: picked.endTime || "",
          startLocation: picked.startLocation?.names || "",
          endLocation: picked.endLocation?.names || "",
        },
        financials: {
          charterFee: fee,
          vat: p.financials.vat ?? 0,
          vatLabel: p.financials.vatLabel || "",
          apa: p.financials.apa ?? 0,
          delivery: p.financials.delivery ?? 0,
          additionalFees: p.financials.additionalFees ?? 0,
          total: finTotal,
          currency: p.financials.currency || offer.currency?.code || "EUR",
        },
        billing: {
          firstName: cust?.firstName || "",
          lastName: cust?.lastName || "",
          email: cust?.email || "",
          phone: cust?.mobile || "",
          street: "", city: "", postalCode: "", country: "",
        },
        schedule: (p.financials.paymentSchedule ?? []).length
          ? (p.financials.paymentSchedule ?? []).map((s, i) => ({
              label: s.label || `Instalment ${s.sequence ?? i + 1}`,
              amount: s.amount ?? 0,
              dueDate: (s.dueDate ?? "").slice(0, 10),
            }))
          : defaultSchedule(),
        stakeholder: { name: p.stakeholder?.name || "", address: p.stakeholder?.address || "", vatNumber: p.stakeholder?.vatNumber || "" },
        owner: { name: p.owner?.name || "", address: p.owner?.address || "", vatNumber: p.owner?.vatNumber || "", commissionInvoiceContact: "" },
      });
      setCommMode("pct"); setCommPct(15); setCommAmt(null); setCard(false);
      setPayRef(`${(cust?.lastName || "CLIENT").toUpperCase()}/${(picked.yacht?.name || "").toUpperCase().replace(/\s+/g, "")}`);
      setStep(2);
    } catch {
      toast({ title: "Parse failed", description: "The contract couldn’t be read.", variant: "destructive" });
    }
    setParsing(false);
  };

  // Live commission off the (editable) charter fee.
  const commission = useMemo(() => {
    const fee = form?.financials.charterFee ?? 0;
    const amount = commMode === "amount" ? commAmt ?? Math.round(fee * 0.15) : Math.round((fee * commPct) / 100);
    const pct = fee ? (amount / fee) * 100 : 0;
    const standard = Math.round(fee * 0.15);
    return { fee, amount, pct, net: fee - amount, standard, overridden: Math.abs(amount - standard) > 0.5 };
  }, [form?.financials.charterFee, commMode, commPct, commAmt]);

  const scheduleTotal = useMemo(() => (form?.schedule ?? []).reduce((s, x) => s + num(x.amount), 0), [form?.schedule]);
  const billingComplete = !!(form?.billing.street && form?.billing.country);
  // Warn (don't block) when the instalments don't add up to the contract total —
  // mis-sized schedules are how clients get under/over-invoiced.
  const scheduleMismatch = !!form && form.schedule.length > 0 && Math.abs(scheduleTotal - num(form.financials.total)) > 1;

  // Step 3 → create the booking from the validated data.
  const create = async () => {
    if (!form || !picked) return;
    setCreating(true);
    try {
      const body = {
        offerId: offer.id,
        offerYachtId: picked.id,
        template: form.template,
        yacht: form.yacht,
        dates: {
          startDate: form.dates.startDate,
          endDate: form.dates.endDate,
          embarkTime: form.dates.embarkTime,
          disembarkTime: form.dates.disembarkTime,
          startLocation: form.dates.startLocation,
          endLocation: form.dates.endLocation,
        },
        financials: {
          charterFee: num(form.financials.charterFee),
          vat: num(form.financials.vat),
          vatLabel: form.financials.vatLabel,
          apa: num(form.financials.apa),
          delivery: num(form.financials.delivery),
          additionalFees: num(form.financials.additionalFees),
          total: num(form.financials.total),
          currency: form.financials.currency,
        },
        commission: { mode: commMode, pct: commPct, amount: commAmt ?? undefined },
        billing: {
          firstName: form.billing.firstName,
          lastName: form.billing.lastName,
          email: form.billing.email,
          phone: form.billing.phone,
          address: {
            streetAddress: form.billing.street,
            city: form.billing.city,
            postalCode: form.billing.postalCode,
            countryRegion: form.billing.country,
          },
        },
        clientPayment: { reference: payRef, card: form.financials.charterFee > CARD_FEE_THRESHOLD ? card : true },
        paymentSchedule: form.schedule.map((s) => ({ label: s.label, amount: num(s.amount), dueDate: s.dueDate || null })),
        stakeholder: form.stakeholder,
        owner: form.owner,
        clientFeedback: { reaction: picked.reaction ?? undefined, note: picked.reactionNote ?? note ?? undefined },
        contractFilename: form.filename,
      };
      const res = await boatsFetch<{ booking: { id: string; slug: string } }>("/bookings/from-contract", "POST", body);
      // Store the signed contract against the booking (Documents) — it then
      // shows on the client + ops booking pages.
      if (file && res?.booking?.id) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("ownerType", "contract");
          fd.append("ownerId", res.booking.id);
          const up = await fetch<{ data?: { url?: string; name?: string } }>("/api/files/upload", "POST", fd);
          // Also attach the stored PDF to the HubSpot deal (non-fatal, don't block the done screen).
          if (up?.data?.url) {
            boatsFetch("/bookings/" + res.booking.id + "/attach-contract", "POST", {
              fileUrl: up.data.url,
              filename: up.data.name || file.name,
              mime: file.type,
            }).catch(() => {});
          } else {
            setContractWarn(true);
          }
        } catch (err) {
          console.error("Contract file upload failed (booking still created)", err);
          // Booking is created, but the signed PDF didn't store — tell the broker.
          setContractWarn(true);
        }
      }
      setCreatedSlug(res?.booking?.slug ?? null);
      setStep(3);
    } catch (e: any) {
      toast({
        title: "Couldn’t create the booking",
        description: e?.context?.message || e?.message || "Please try again.",
        variant: "destructive",
      });
    }
    setCreating(false);
  };

  const setF = (patch: Partial<ConvertForm>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const setFin = (patch: Partial<ConvertForm["financials"]>) => setForm((f) => (f ? { ...f, financials: { ...f.financials, ...patch } } : f));
  const setBill = (patch: Partial<ConvertForm["billing"]>) => setForm((f) => (f ? { ...f, billing: { ...f.billing, ...patch } } : f));

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="w-[95vw] max-w-3xl gap-0 p-0 overflow-hidden">
        <DialogHeader className="flex flex-row items-center gap-5 border-b border-blue-100 px-6 py-4 space-y-0">
          <div className="min-w-0">
            <div className="text-xs text-slate-500 truncate">
              {ref} · {[offer.customer?.firstName, offer.customer?.lastName].filter(Boolean).join(" ")}
              {step > 0 && picked ? ` · ${picked.yacht?.name}` : ""}
            </div>
            <DialogTitle className="text-lg">Convert to booking</DialogTitle>
          </div>
          <div className="ml-auto hidden w-[380px] shrink-0 lg:block">
            <Steps items={STEPS} step={STEPS[step]} />
          </div>
        </DialogHeader>

        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          {/* STEP 0 — CHOOSE YACHT */}
          {step === 0 && (
            <div className="flex flex-col gap-3">
              {noFeedback ? (
                <div className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
                  <AlertTriangle size={18} className="shrink-0 text-amber-500" />
                  <p>
                    <b>No client feedback on this offer yet.</b> They may have replied by phone or email — choose the
                    yacht they’re going ahead with and note what they said.
                  </p>
                </div>
              ) : (
                <div className="flex gap-2.5 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm">
                  <Info size={18} className="shrink-0 text-blue-500" />
                  <p>
                    <b>{yachts.length} yacht{yachts.length === 1 ? " was" : "s were"} offered.</b> Only the one you pick
                    converts; the rest stay on the offer as history.
                  </p>
                </div>
              )}

              {yachts.map((oy) => {
                const selected = pick === oy.id;
                const r = oy.reaction ? REACTION[oy.reaction] : null;
                return (
                  <button
                    type="button"
                    key={oy.id}
                    onClick={() => setPick(oy.id)}
                    className={clsx(
                      "flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition",
                      selected ? "border-blue-400 bg-blue-50/60 ring-1 ring-blue-300" : "border-blue-100 hover:bg-slate-50",
                    )}
                  >
                    <span
                      className={clsx(
                        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                        selected ? "border-blue-500 bg-blue-500 text-white" : "border-slate-300",
                      )}
                    >
                      {selected && <Check size={13} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-1.5 font-medium">
                        {oy.yacht?.name}
                        <Badge className="bg-blue-100 text-blue-700">
                          {PROVIDER_LABEL[oy.yacht?.provider] ?? oy.yacht?.provider}
                        </Badge>
                        {r && !noFeedback ? <Badge className={r.cls}>{r.label}</Badge> : <Badge className="bg-slate-100 text-slate-500">No reaction</Badge>}
                      </span>
                      <span className="block text-sm text-slate-500">{yachtSpec(oy)}</span>
                      {!noFeedback && oy.reactionNote && (
                        <span className="mt-1.5 flex gap-1.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-sm text-slate-600">
                          <Info size={15} className="mt-0.5 shrink-0 text-slate-400" />
                          <span>
                            “{oy.reactionNote}”<span className="ml-1 text-slate-400">— {offer.customer?.firstName}</span>
                          </span>
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="font-mono font-semibold">{money(yachtFee(oy), offer.currency?.code)}</span>
                      <span className="block text-xs text-slate-400">charter fee</span>
                    </span>
                  </button>
                );
              })}

              {noFeedback && (
                <div className="rounded-lg border border-blue-100 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="font-semibold">What did the client say?</h3>
                    <span className="text-xs text-slate-400">optional · saved with the booking</span>
                  </div>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. Confirmed on the phone — wants the same crew as last summer…" />
                </div>
              )}

              {picked && !needsContract(picked.yacht?.provider) && (
                <div className="flex gap-2.5 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm">
                  <Info size={18} className="shrink-0 text-blue-500" />
                  <p>
                    <b>{PROVIDER_LABEL[picked.yacht?.provider] ?? picked.yacht?.provider} booking — no contract to parse.</b>{" "}
                    We option <b>{picked.yacht?.name}</b> with the operator and open the booking summary.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 1 — UPLOAD */}
          {step === 1 && picked && (
            <div className="flex flex-col gap-3">
              <div className="flex gap-2.5 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm">
                <Info size={18} className="shrink-0 text-blue-500" />
                <p>
                  Upload the signed <b>{needsContract(picked.yacht?.provider) ? "MYBA or CYBA" : ""}</b> contract for{" "}
                  <b>{picked.yacht?.name}</b>. We parse it, then you validate every field before the booking is created.
                </p>
              </div>
              <input
                ref={fileInput}
                type="file"
                accept=".pdf,.docx,.txt,application/pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {parsing ? (
                <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-5 py-8">
                  <Loader2 size={22} className="animate-spin text-blue-500" />
                  <div>
                    <b>Parsing contract…</b>
                    <div className="text-sm text-slate-500">Template · financials · dates &amp; times · locations · instalments · owner</div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-blue-200 px-5 py-8 text-center hover:bg-slate-50"
                >
                  <UploadCloud size={28} className="text-blue-400" />
                  <b>{file ? file.name : "Choose the signed contract"}</b>
                  <span className="text-sm text-slate-500">PDF, DOCX, or OCR text</span>
                </button>
              )}
            </div>
          )}

          {/* STEP 2 — VALIDATE */}
          {step === 2 && form && (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-blue-100 p-4">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-slate-500" />
                  <b>Contract — {form.template.toUpperCase()}</b>
                  <Badge className="bg-green-100 text-green-700"><Check size={11} /> parsed</Badge>
                  <span className="ml-auto text-xs text-slate-400">{form.filename}</span>
                </div>
                {!billingComplete && (
                  <div className="mt-3 flex gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                    <AlertTriangle size={16} className="shrink-0 text-amber-500" />
                    <p>Billing address + country are required before we can invoice the client.</p>
                  </div>
                )}
              </div>

              <Group title="Yacht">
                <Row label="Yacht" filled={!!form.yacht.name}><Input value={form.yacht.name} onChange={(e) => setF({ yacht: { ...form.yacht, name: e.target.value } })} /></Row>
                <Row label="Type & length" filled><Input value={form.yacht.type} onChange={(e) => setF({ yacht: { ...form.yacht, type: e.target.value } })} /></Row>
                <Row label="Flag" filled><Input value={form.yacht.flag} onChange={(e) => setF({ yacht: { ...form.yacht, flag: e.target.value } })} /></Row>
              </Group>

              <Group title="Dates, times & locations">
                <Row label="Start date" filled={!!form.dates.startDate}><Input type="date" value={form.dates.startDate} onChange={(e) => setF({ dates: { ...form.dates, startDate: e.target.value } })} /></Row>
                <Row label="Embarkation time" filled><Input value={form.dates.embarkTime} onChange={(e) => setF({ dates: { ...form.dates, embarkTime: e.target.value } })} /></Row>
                <Row label="Start location" filled={!!form.dates.startLocation}><Input value={form.dates.startLocation} onChange={(e) => setF({ dates: { ...form.dates, startLocation: e.target.value } })} /></Row>
                <Row label="Finish date" filled={!!form.dates.endDate}><Input type="date" value={form.dates.endDate} onChange={(e) => setF({ dates: { ...form.dates, endDate: e.target.value } })} /></Row>
                <Row label="Disembarkation time" filled><Input value={form.dates.disembarkTime} onChange={(e) => setF({ dates: { ...form.dates, disembarkTime: e.target.value } })} /></Row>
                <Row label="Finish location" filled={!!form.dates.endLocation}><Input value={form.dates.endLocation} onChange={(e) => setF({ dates: { ...form.dates, endLocation: e.target.value } })} /></Row>
              </Group>

              <Group title="Money">
                <Row label="Charter fee" hint="writes to the deal amount" filled={form.financials.charterFee > 0}><MoneyInput value={form.financials.charterFee} onChange={(n) => setFin({ charterFee: n })} /></Row>
                <Row label="VAT" hint={form.financials.vatLabel} filled><MoneyInput value={form.financials.vat} onChange={(n) => setFin({ vat: n })} /></Row>
                <Row label="APA" filled><MoneyInput value={form.financials.apa} onChange={(n) => setFin({ apa: n })} /></Row>
                <Row label="Delivery fees" filled><MoneyInput value={form.financials.delivery} onChange={(n) => setFin({ delivery: n })} /></Row>
                <Row label="Additional fees" filled><MoneyInput value={form.financials.additionalFees} onChange={(n) => setFin({ additionalFees: n })} /></Row>
                <Row label="Total" filled><MoneyInput value={form.financials.total} onChange={(n) => setFin({ total: n })} /></Row>
                <Row label="Currency" filled><Input value={form.financials.currency} onChange={(e) => setFin({ currency: e.target.value.toUpperCase() })} /></Row>
              </Group>

              <Group title="Commission & net charter fee">
                <div className="mb-3 inline-flex rounded-md border border-blue-100 p-0.5 text-sm">
                  <button className={clsx("rounded px-3 py-1", commMode === "pct" && "bg-blue-100 text-blue-700")} onClick={() => setCommMode("pct")}>Percentage</button>
                  <button className={clsx("rounded px-3 py-1", commMode === "amount" && "bg-blue-100 text-blue-700")} onClick={() => { setCommMode("amount"); if (commAmt == null) setCommAmt(commission.amount); }}>Keyed amount</button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    {commMode === "pct" ? (
                      <label className="text-sm">Commission %<Input type="number" step="0.5" min={0} max={100} value={commPct} onChange={(e) => setCommPct(Math.max(0, Math.min(100, num(e.target.value))))} /><span className="text-xs text-slate-400">of charter fee</span></label>
                    ) : (
                      <label className="text-sm">Commission amount<MoneyInput value={commAmt ?? commission.amount} onChange={(n) => setCommAmt(n)} /><span className="text-xs text-slate-400">{form.financials.currency}</span></label>
                    )}
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Commission</span><b className="font-mono">{money(commission.amount)} <span className="text-slate-400">({commission.pct.toFixed(1)}%)</span></b></div>
                    <div className="mt-1 flex justify-between border-t pt-1"><span className="text-slate-500">Net charter fee → HubSpot</span><b className="font-mono">{money(commission.net)}</b></div>
                    <div className="mt-1 flex justify-between"><span className="text-slate-500">App assumed 15%</span><span>{money(commission.standard)}</span></div>
                    <div className="mt-1">{commission.overridden ? <Badge className="bg-amber-100 text-amber-700">overridden</Badge> : <Badge className="bg-green-100 text-green-700"><Check size={11} /> standard</Badge>}</div>
                  </div>
                </div>
              </Group>

              <Group title="Client & billing">
                <Row label="Client name" filled><Input value={`${form.billing.firstName} ${form.billing.lastName}`.trim()} onChange={(e) => { const [f, ...r] = e.target.value.split(" "); setBill({ firstName: f, lastName: r.join(" ") }); }} /></Row>
                <Row label="Email" filled={!!form.billing.email}><Input value={form.billing.email} onChange={(e) => setBill({ email: e.target.value })} /></Row>
                <Row label="Billing address" hint="required for the invoice" required filled={!!form.billing.street}><Input value={form.billing.street} onChange={(e) => setBill({ street: e.target.value })} placeholder="Street address" /></Row>
                <Row label="City" filled><Input value={form.billing.city} onChange={(e) => setBill({ city: e.target.value })} /></Row>
                <Row label="Postcode" filled><Input value={form.billing.postalCode} onChange={(e) => setBill({ postalCode: e.target.value })} /></Row>
                <Row label="Country" required filled={!!form.billing.country}><Input value={form.billing.country} onChange={(e) => setBill({ country: e.target.value })} /></Row>
                <Row label="Phone" filled><Input value={form.billing.phone} onChange={(e) => setBill({ phone: e.target.value })} /></Row>
              </Group>

              <Group title="Payment schedule">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-slate-500">{form.schedule.length} instalment{form.schedule.length === 1 ? "" : "s"} · created expected · unpaid</span>
                  <Button variant="link" size="sm" onClick={() => setF({ schedule: [...form.schedule, { label: `Instalment ${form.schedule.length + 1}`, amount: 0, dueDate: "" }] })}>+ Add instalment</Button>
                </div>
                {form.schedule.map((s, i) => (
                  <div key={i} className="mb-2 grid grid-cols-[1fr_120px_140px_auto] items-center gap-2">
                    <Input value={s.label} onChange={(e) => setF({ schedule: form.schedule.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} placeholder="Label" />
                    <MoneyInput value={s.amount} onChange={(n) => setF({ schedule: form.schedule.map((x, j) => (j === i ? { ...x, amount: n } : x)) })} placeholder="Amount" />
                    <Input type="date" value={s.dueDate} onChange={(e) => setF({ schedule: form.schedule.map((x, j) => (j === i ? { ...x, dueDate: e.target.value } : x)) })} />
                    <button className="text-slate-400 hover:text-red-500" onClick={() => setF({ schedule: form.schedule.filter((_, j) => j !== i) })}>✕</button>
                  </div>
                ))}
                <div className="mt-1 flex justify-between border-t pt-2 text-sm">
                  <span className="font-medium">Scheduled total</span>
                  <span className={clsx("font-mono font-medium", scheduleMismatch && "text-amber-600")}>{money(scheduleTotal)}</span>
                </div>
                {scheduleMismatch && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>The instalments ({money(scheduleTotal)}) don’t add up to the contract total ({money(num(form.financials.total))}). Check the schedule before creating the booking.</span>
                  </div>
                )}
              </Group>

              <Group title="Where the client pays">
                <p className="text-sm text-slate-500">Client funds always come into a {offer.brand === "anchor" ? "Anchor" : "HELM"} account — never the stakeholder’s.</p>
                <Row label="Account" hint={`${offer.brand === "anchor" ? "Anchor" : "HELM"} · ${form.financials.currency}`} filled>
                  <div className="text-sm">{offer.brand === "anchor" ? "Anchor" : "HELM"} · {form.financials.currency} client account <Badge className="bg-green-100 text-green-700">matches charter currency</Badge></div>
                </Row>
                <Row label="Payment reference" filled><Input value={payRef} onChange={(e) => setPayRef(e.target.value)} /></Row>
                <label className="mt-2 flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" checked={form.financials.charterFee <= CARD_FEE_THRESHOLD ? true : card} disabled={form.financials.charterFee <= CARD_FEE_THRESHOLD} onChange={() => setCard((c) => !c)} />
                  <span>
                    <b>Offer card payment as well</b>
                    <br />
                    <span className="text-xs text-slate-500">
                      {form.financials.charterFee <= CARD_FEE_THRESHOLD
                        ? `Card is free below ${money(CARD_FEE_THRESHOLD)} — offered by default.`
                        : `Card fees apply above ${money(CARD_FEE_THRESHOLD)}. Leave off for bank transfer only.`}
                    </span>
                  </span>
                </label>
              </Group>

              <Group title="Parties">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Stakeholder — supplier we pay (HubSpot company)</div>
                <Row label="Stakeholder" filled={!!form.stakeholder.name}><Input value={form.stakeholder.name} onChange={(e) => setF({ stakeholder: { ...form.stakeholder, name: e.target.value } })} /></Row>
                <Row label="Address" filled><Input value={form.stakeholder.address} onChange={(e) => setF({ stakeholder: { ...form.stakeholder, address: e.target.value } })} /></Row>
                <Row label="VAT number" filled><Input value={form.stakeholder.vatNumber} onChange={(e) => setF({ stakeholder: { ...form.stakeholder, vatNumber: e.target.value } })} /></Row>
                <div className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">Yacht owner — stored for the commission invoice</div>
                <Row label="Owner / company" filled={!!form.owner.name}><Input value={form.owner.name} onChange={(e) => setF({ owner: { ...form.owner, name: e.target.value } })} /></Row>
                <Row label="Registered address" filled><Input value={form.owner.address} onChange={(e) => setF({ owner: { ...form.owner, address: e.target.value } })} /></Row>
                <Row label="VAT number" filled><Input value={form.owner.vatNumber} onChange={(e) => setF({ owner: { ...form.owner, vatNumber: e.target.value } })} /></Row>
              </Group>

              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 text-sm">
                <div className="mb-2 font-medium">Writes to HubSpot on confirm</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    "amount → charter fee",
                    "deal_currency_code",
                    `net_charter_fee (${money(commission.net)})`,
                    "net_charter_fee_currency",
                    `contract_template → ${form.template.toUpperCase()}`,
                    "dates + times + locations",
                    `supplier → ${form.stakeholder.name || "—"}`,
                    "contact billing address",
                    "dealstage → Contract out",
                  ].map((x) => (
                    <Badge key={x} className="bg-white text-slate-600 border border-blue-100"><Check size={11} className="text-green-600" /> {x}</Badge>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">All non-fatal — a failed write is logged, it never fails the conversion.</p>
              </div>
            </div>
          )}

          {/* STEP 3 — DONE */}
          {step === 3 && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 size={44} className="text-green-500" />
              <h3 className="text-xl font-semibold">Booking created from contract</h3>
              <p className="max-w-md text-sm text-slate-500">
                {form?.yacht.name} · {[offer.customer?.firstName, offer.customer?.lastName].filter(Boolean).join(" ")}. Instalments set as expected with no payments attached; the deal has moved to <b>Contract out</b>.
              </p>
              {createdSlug && <div className="rounded-md bg-slate-100 px-4 py-2 text-sm">Booking ref · <b>{createdSlug}</b></div>}
              {contractWarn && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-left text-sm text-amber-700">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>The booking was created, but the signed contract PDF couldn’t be stored — upload it on the booking’s Documents.</span>
                </div>
              )}
              <div className="mt-2 rounded-lg border border-blue-100 p-4 text-left text-sm">
                <div className="mb-1 font-medium">Next</div>
                <ul className="list-disc pl-5 text-slate-600">
                  <li>Countersigned contract back → upload to the booking’s Documents</li>
                  <li>First instalment received → matches in the payments inbox → booking confirms</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center gap-3 border-t border-blue-100 px-6 py-4">
          <span className="mr-auto text-sm text-slate-500">
            {step === 0 && !picked && "Select the yacht the client is going ahead with"}
            {step === 0 && picked && needsContract(picked.yacht?.provider) && `Converting ${picked.yacht?.name} · requires a signed MYBA or CYBA contract`}
            {step === 0 && picked && !needsContract(picked.yacht?.provider) && `${picked.yacht?.name} · ${PROVIDER_LABEL[picked.yacht?.provider] ?? picked.yacht?.provider} — we option the yacht`}
            {step === 2 && form && `Total ${money(num(form.financials.total))} · ${form.schedule.length} instalments · contract saved to booking + HubSpot`}
            {step === 3 && "Booking created · deal moved to Contract out"}
          </span>

          {step === 0 && (
            <>
              <Button variant="link" onClick={close}>Cancel</Button>
              {picked && needsContract(picked.yacht?.provider) ? (
                <Button className="gap-2" onClick={() => setStep(1)}>Upload contract <ArrowRight size={16} /></Button>
              ) : (
                <Button className="gap-2" disabled={!picked} onClick={optionAndOpenSummary}>Option yacht &amp; open summary <ArrowRight size={16} /></Button>
              )}
            </>
          )}
          {step === 1 && (
            <>
              <Button variant="outline" className="gap-1" onClick={() => setStep(0)}><ArrowLeft size={16} /> Change yacht</Button>
              <Button variant="link" onClick={close}>Cancel</Button>
              <Button className="gap-2" disabled={!file || parsing} onClick={parse}>{parsing ? "Parsing…" : "Parse contract"} {!parsing && <ArrowRight size={16} />}</Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Re-upload</Button>
              <Button className="gap-2" disabled={creating || !billingComplete} onClick={create}>{creating ? "Creating…" : "Create booking & write to HubSpot"} {!creating && <ArrowRight size={16} />}</Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="link" onClick={close}>Close</Button>
              {createdSlug && (
                <Button className="gap-2" onClick={() => { window.open(`${publicHost(offer.brand)}/booking/details/${createdSlug}`, "_blank", "noopener,noreferrer"); }}>View client booking page <ArrowRight size={16} /></Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
